import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as annexesApi from './api'
import type { PatchAnnexRequest, PublishAnnexRequest } from './types'

export const annexKeys = {
  list: (contractId: number, includeCancelled: boolean) =>
    ['annexes', 'list', contractId, includeCancelled] as const,
  one: (contractId: number, annexId: number) =>
    ['annexes', 'one', contractId, annexId] as const,
  annexableFields: (contractId: number) =>
    ['annexes', 'annexableFields', contractId] as const,
  parametersSchema: (contractId: number) =>
    ['annexes', 'parametersSchema', contractId] as const,
}

function invalidateAnnexes(qc: ReturnType<typeof useQueryClient>, contractId: number) {
  qc.invalidateQueries({ queryKey: ['annexes', 'list', contractId] })
  qc.invalidateQueries({ queryKey: ['annexes', 'one', contractId] })
  // Publish touches the contract aggregate (extras + version + core columns); refresh it too.
  qc.invalidateQueries({ queryKey: ['contract', contractId] })
  qc.invalidateQueries({ queryKey: ['contracts'] })
}

export function useAnnexes(contractId: number, includeCancelled = false) {
  return useQuery({
    queryKey: annexKeys.list(contractId, includeCancelled),
    queryFn: () => annexesApi.listAnnexes(contractId, includeCancelled),
  })
}

export function useAnnex(contractId: number, annexId: number | null) {
  return useQuery({
    queryKey: annexKeys.one(contractId, annexId ?? 0),
    queryFn: () => annexesApi.getAnnex(contractId, annexId as number),
    enabled: annexId !== null,
  })
}

export function useAnnexableFields(contractId: number, enabled = true) {
  return useQuery({
    queryKey: annexKeys.annexableFields(contractId),
    queryFn: () => annexesApi.getAnnexableFields(contractId),
    enabled,
  })
}

export function useAnnexParametersSchema(contractId: number, enabled = true) {
  return useQuery({
    queryKey: annexKeys.parametersSchema(contractId),
    queryFn: () => annexesApi.getAnnexParametersSchema(contractId),
    enabled,
  })
}

export function useCreateAnnexDraft(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => annexesApi.createAnnexDraft(contractId),
    onSuccess: () => invalidateAnnexes(qc, contractId),
  })
}

export function usePatchAnnexDraft(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { annexId: number; payload: PatchAnnexRequest }) =>
      annexesApi.patchAnnexDraft(contractId, vars.annexId, vars.payload),
    onSuccess: () => invalidateAnnexes(qc, contractId),
  })
}

export function usePublishAnnex(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { annexId: number; payload: PublishAnnexRequest }) =>
      annexesApi.publishAnnex(contractId, vars.annexId, vars.payload),
    onSuccess: () => invalidateAnnexes(qc, contractId),
  })
}

export function useCancelAnnexDraft(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { annexId: number; expectedVersion: number }) =>
      annexesApi.cancelAnnexDraft(contractId, vars.annexId, vars.expectedVersion),
    onSuccess: () => invalidateAnnexes(qc, contractId),
  })
}
